import { Button, CloseButton, FileButton, Menu, Tooltip, UnstyledButton } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Box, Film, Image as ImageIcon, LibraryBig, Mic, Sparkles, Upload, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";

import { MediaMime } from "../../../api/generated/zod";
import { useGenerateImage } from "../../../platform/media/GenerateImageDialog";
import { IMAGE_TYPES, pictureOf, type PickedImage } from "../../../platform/media/ImagePicker";
import { UploadState, useImageUpload } from "../../../platform/media/image-upload";
import type { Composer } from "../composer/use-composer";
import { ProductPicker } from "../../../platform/media/ProductPicker";
import { ReferencesDialog } from "../../../platform/media/ReferencesDialog";
import { elementProblems, NO_ELEMENT, type ElementDraft, type ElementVoice } from "./elements";
import classes from "./elements.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId";
const VIDEO_TYPES = MediaMime.options.filter((mime) => mime.startsWith("video/"));

/** Live's tips on the three parts (`composer-raw.md` "Elements"). */
const TIPS = {
  frontal: "The frontal image of the element (main view).",
  images: "Additional reference images from different angles",
  video: "The video of the element. A request can only have one element with a video.",
  // Ours: live has no voice on an element (spec G2-kling-element-voice-picker).
  voice:
    "The voice this element speaks with. It still needs a frontal image and one more image, or a video.",
} as const;

/** The video's tip: live's words where the model takes one video element, else the model's own
 * count (Kling 4.0's 3, live UI bug 143; spec web/edit-reference-videos). */
const videoTip = (most: number | undefined) =>
  (most ?? 1) <= 1
    ? TIPS.video
    : `The video of the element. A request can have up to ${most} elements with a video.`;

type Part = "frontal" | "images" | "video";

/** The element's voice, where the model's block takes one: the page keeps it and its panel. */
export interface ElementVoicePart {
  picked: ElementVoice | null;
  /** The name of the voice picked, once known. */
  name: string | null;
  /** The block's most voices, reached by the other elements: this one can take none. */
  room: number;
  full: boolean;
  onOpen: () => void;
  onRemove: () => void;
}

export interface ElementsEditorProps {
  c: Composer;
  /** The element being edited, or null for a new one. */
  editing: number | null;
  onClose: () => void;
  voice?: ElementVoicePart | undefined;
}

/**
 * Live's "Create Element" (#229, capture 16, LEFTOVERS 2): over the composer, its three parts as
 * tiles with live's tips, each opening live's menu (From library, Generate, Upload, From
 * product); "Add Element" stays grey, its tooltip live's reasons, until the element is one the
 * model takes. An element opened again is "Edit Element" with "Save Element". Nothing is sent:
 * the element joins the draft.
 */
export function ElementsEditor({ c, editing, onClose, voice }: ElementsEditorProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const caps = c.elementCaps;
  const [element, setElement] = useState<ElementDraft>(
    () => (editing === null ? undefined : c.draft.elements[editing]) ?? NO_ELEMENT,
  );
  const [picking, setPicking] = useState<Part | null>(null);
  const [productsFor, setProductsFor] = useState<"frontal" | "images" | null>(null);
  if (caps === null) return null;
  const imagesMax = caps.images.max;
  const add = (part: Part, image: PickedImage) =>
    setElement((current) => {
      if (part === "frontal") return { ...current, frontal: image };
      if (part === "video") return { ...current, video: image };
      return current.images.length >= imagesMax
        ? current
        : { ...current, images: [...current.images, image] };
    });
  const problems = elementProblems(element, caps);
  const save = () => {
    // A model that takes no voice keeps the element's for the one that does, unsent.
    const made = voice === undefined ? element : { ...element, voice: voice.picked };
    if (editing === null) c.addElement(made);
    else c.replaceElement(editing, made);
    onClose();
  };
  const productsTarget = productsFor ?? "frontal";
  return (
    <div className={classes.editor}>
      <div className={classes.header}>
        <h2 className={classes.title}>{editing === null ? "Create Element" : "Edit Element"}</h2>
        <CloseButton aria-label="Close the element" onClick={onClose} />
      </div>
      <ProductPicker
        teamId={teamId}
        brandId={brandId}
        chosen={null}
        opened={productsFor !== null}
        onClose={() => setProductsFor(null)}
        onConfirm={(choice) =>
          add(productsTarget, { assetId: choice.assetId, url: choice.url, name: choice.name })
        }
        anchor={(wrap) => (
          // A part the model takes none of is not shown, as live's editor does (w5, issue 284).
          <div className={classes.parts}>
            {caps.frontal === "none" ? null : (
              <PartTile
                label="Frontal Image"
                tip={TIPS.frontal}
                wide
                teamId={teamId}
                brandId={brandId}
                pictures={element.frontal === null ? [] : [element.frontal]}
                onRemove={() => setElement((current) => ({ ...current, frontal: null }))}
                onFromLibrary={() => setPicking("frontal")}
                onFromProduct={() => setProductsFor("frontal")}
                onUploaded={(image) => add("frontal", image)}
                onGenerated={(image) => add("frontal", image)}
                wrap={productsTarget === "frontal" ? wrap : asIs}
              />
            )}
            {imagesMax === 0 ? null : (
              <PartTile
                label="Image Ref"
                tip={TIPS.images}
                teamId={teamId}
                brandId={brandId}
                pictures={element.images}
                full={element.images.length >= imagesMax}
                onRemove={(assetId) =>
                  setElement((current) => ({
                    ...current,
                    images: current.images.filter((each) => each.assetId !== assetId),
                  }))
                }
                onFromLibrary={() => setPicking("images")}
                onFromProduct={() => setProductsFor("images")}
                onUploaded={(image) => add("images", image)}
                onGenerated={(image) => add("images", image)}
                wrap={productsTarget === "images" ? wrap : asIs}
              />
            )}
            {caps.video === "none" ? null : (
              <PartTile
                label="Video"
                tip={videoTip(caps.max_videos)}
                kind="video"
                teamId={teamId}
                brandId={brandId}
                pictures={element.video === null ? [] : [element.video]}
                onRemove={() => setElement((current) => ({ ...current, video: null }))}
                onFromLibrary={() => setPicking("video")}
                onUploaded={(image) => add("video", image)}
                wrap={asIs}
              />
            )}
            {voice === undefined ? null : <VoicePart voice={voice} />}
          </div>
        )}
      />
      <div className={classes.footer}>
        <Tooltip
          label={problems.join("\n")}
          disabled={problems.length === 0}
          withArrow
          multiline
          maw={300}
        >
          <span>
            <Button disabled={problems.length > 0} onClick={save}>
              {editing === null ? "Add Element" : "Save Element"}
            </Button>
          </span>
        </Tooltip>
      </div>
      {picking === null ? null : (
        <ReferencesDialog
          teamId={teamId}
          brandId={brandId}
          {...DIALOGS[picking](imagesMax)}
          max={picking === "images" ? imagesMax : 1}
          chosen={(picking === "images"
            ? element.images
            : [picking === "frontal" ? element.frontal : element.video].filter(
                (each) => each !== null,
              )
          ).map((each) => each.assetId)}
          onClose={() => setPicking(null)}
          onConfirm={(images) =>
            setElement((current) => {
              if (picking === "images") return { ...current, images };
              const image = images[0];
              if (image === undefined) return current;
              return picking === "frontal"
                ? { ...current, frontal: image }
                : { ...current, video: image };
            })
          }
        />
      )}
    </div>
  );
}

const asIs = (element: ReactElement) => element;

/** Live's library dialog for each part (`composer-raw.md` "Elements"). */
const DIALOGS: Record<
  Part,
  (max: number) => { title: string; subtitle?: string; kind?: "image" | "video" }
> = {
  frontal: () => ({
    title: "Select Frontal Image",
    subtitle: "Choose a frontal image for this element",
  }),
  images: (max) => ({
    title: "Select Reference Images",
    subtitle: `Choose reference images for this element · Select up to ${max}`,
  }),
  video: () => ({ title: "Select Video", kind: "video" }),
};

/**
 * The element's voice (ours, spec G2-kling-element-voice-picker; live has no voice on an element):
 * a part's tile opening Voice Settings, then the voice's name with an x, which opens it again.
 */
function VoicePart({ voice }: { voice: ElementVoicePart }) {
  if (voice.picked !== null) {
    const name = voice.name ?? "A voice";
    return (
      <span className={classes.partGroup}>
        <span className={classes.picture}>
          <UnstyledButton
            className={classes.voicePicked}
            aria-label={`Voice: ${name}`}
            onClick={voice.onOpen}
          >
            <Mic size={20} strokeWidth={1.5} aria-hidden />
            <span className={classes.voicePickedName}>{name}</span>
          </UnstyledButton>
          <UnstyledButton
            className={classes.pictureRemove}
            aria-label={`Remove ${name}`}
            onClick={voice.onRemove}
          >
            <X size={12} strokeWidth={2.5} aria-hidden />
          </UnstyledButton>
        </span>
      </span>
    );
  }
  return (
    <span className={classes.partGroup}>
      <Tooltip
        label={voice.full ? `Up to ${voice.room} voices a video.` : TIPS.voice}
        withArrow
        multiline
        maw={260}
      >
        <span className={classes.partWrap}>
          <UnstyledButton
            className={classes.part}
            aria-label="Voice"
            disabled={voice.full}
            onClick={voice.onOpen}
          >
            <Mic size={20} strokeWidth={1.5} aria-hidden />
            <span className={classes.partLabel}>Voice</span>
          </UnstyledButton>
        </span>
      </Tooltip>
    </span>
  );
}

interface PartTileProps {
  label: string;
  tip: string;
  /** A video part takes videos (library, upload); its menu has no Generate or From product. */
  kind?: "image" | "video";
  /** Live's frontal tile is wider than the others. */
  wide?: boolean;
  teamId: string;
  brandId: string;
  pictures: PickedImage[];
  /** No more fit: the tile greys out. */
  full?: boolean;
  onRemove: (assetId: string) => void;
  onFromLibrary: () => void;
  onFromProduct?: () => void;
  onUploaded: (image: PickedImage) => void;
  /** Live's "Generate" for an image part (#985): the image made in its dialog, once saved. */
  onGenerated?: (image: PickedImage) => void;
  wrap: (element: ReactElement) => ReactElement;
}

/** One part of the element: its pictures, then its tile and live's menu. */
function PartTile(props: PartTileProps) {
  const { label, tip, wide = false, pictures, full = false, kind = "image" } = props;
  const resetChooser = useRef<() => void>(null);
  const running = useRef<AbortController | null>(null);
  useEffect(() => () => running.current?.abort(), []);
  // The part keeps its image made and not saved while the editor is open.
  const generate = useGenerateImage({
    teamId: props.teamId,
    brandId: props.brandId,
    onSaved: (asset) =>
      props.onGenerated?.({ assetId: asset.id, url: pictureOf(asset), name: asset.name }),
  });
  const upload = useImageUpload({
    teamId: props.teamId,
    brandId: props.brandId,
    kind,
    onUploaded: (asset) =>
      props.onUploaded({
        assetId: asset.id,
        url: kind === "video" ? (asset.urls.poster ?? null) : pictureOf(asset),
        name: asset.name,
      }),
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  const tile = (
    <UnstyledButton
      className={classes.part}
      data-wide={wide || undefined}
      aria-label={label}
      disabled={full}
    >
      {kind === "video" ? (
        <Film size={20} strokeWidth={1.5} aria-hidden />
      ) : (
        <ImageIcon size={20} strokeWidth={1.5} aria-hidden />
      )}
      <span className={classes.partLabel}>{label}</span>
    </UnstyledButton>
  );
  const menu: ReactNode = (
    <FileButton
      accept={(kind === "video" ? VIDEO_TYPES : IMAGE_TYPES).join(",")}
      resetRef={resetChooser}
      onChange={(file) => {
        void upload.send(file);
        resetChooser.current?.();
      }}
      inputProps={{ "aria-label": `Choose a ${label.toLowerCase()} to upload` }}
    >
      {(chooser) => (
        <Menu position="top-start" disabled={full}>
          <Menu.Target>{tile}</Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<LibraryBig size={16} />} onClick={props.onFromLibrary}>
              From library
            </Menu.Item>
            {props.onGenerated === undefined ? null : (
              <Menu.Item leftSection={<Sparkles size={16} />} onClick={generate.open}>
                Generate
              </Menu.Item>
            )}
            <Menu.Item leftSection={<Upload size={16} />} onClick={chooser.onClick}>
              Upload
            </Menu.Item>
            {props.onFromProduct === undefined ? null : (
              <Menu.Item leftSection={<Box size={16} />} onClick={props.onFromProduct}>
                From product
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      )}
    </FileButton>
  );
  return (
    <span className={classes.partGroup}>
      {pictures.map((picture) => (
        <span key={picture.assetId} className={classes.picture}>
          {picture.url === null ? (
            <ImageIcon size={20} strokeWidth={1.5} aria-hidden />
          ) : (
            <img src={picture.url} alt={picture.name} />
          )}
          <UnstyledButton
            className={classes.pictureRemove}
            aria-label={`Remove ${picture.name}`}
            onClick={() => props.onRemove(picture.assetId)}
          >
            <X size={12} strokeWidth={2.5} aria-hidden />
          </UnstyledButton>
        </span>
      ))}
      <Tooltip label={tip} withArrow multiline maw={260}>
        {props.wrap(<span className={classes.partWrap}>{menu}</span>)}
      </Tooltip>
      <UploadState upload={upload} />
      {generate.dialog}
    </span>
  );
}
